/**
 * MuroComentarios: árbol de comentarios estilo Facebook.
 *
 * Render strategy:
 *   - Aplanamos el árbol en JS (no en JSX) y renderizamos una lista plana de
 *     <ComentarioRow>. Cada Row recibe `nivel` y aplica sangría. Esto evita
 *     que el componente se referencie a sí mismo dentro de JSX, lo cual
 *     dispara `Maximum call stack size exceeded` en el plugin babel
 *     visual-edits (resuelve bindings de JSX recursivamente sin "visited" set).
 */
import React, { useState, useMemo } from 'react';
import ReactionPicker, { getReactionMeta } from './ReactionPicker';
import MentionInput from './MentionInput';
import { MoreVertical, MessageCircle, Edit2, Trash2, X } from 'lucide-react';
import { formatBsAs } from '../../utils/fechaUtils';

const MAX_INDENT = 2;

function ComentarioRow(props) {
  const {
    comentario,
    hijosCount,
    nivel,
    expanded,
    onToggleExpanded,
    currentUserId,
    esStaff,
    spaceId,
    token,
    onResponder,
    onEditar,
    onEliminar,
    onReaccionar,
  } = props;

  const [replyOpen, setReplyOpen] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyMenciones, setReplyMenciones] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editText, setEditText] = useState(comentario.contenido || '');
  const [editMenciones, setEditMenciones] = useState(comentario.menciones || []);
  const [menuOpen, setMenuOpen] = useState(false);

  const esAutor = comentario.autor_id === currentUserId;
  const puedeEliminar = esAutor || esStaff;
  const indent = Math.min(nivel, MAX_INDENT);
  const indentPx = indent === 0 ? 0 : indent === 1 ? 24 : 40;
  const borderClass = nivel > 0 ? 'border-l-2 border-gray-200 pl-3' : '';

  const totalReacciones = useMemo(() => {
    const r = comentario.reacciones || {};
    return Object.values(r).reduce((a, b) => a + (b || 0), 0);
  }, [comentario.reacciones]);

  const enviarRespuesta = () => {
    if (!replyText.trim()) return;
    onResponder({
      contenido: replyText.trim(),
      respuesta_a: comentario.id,
      menciones: replyMenciones,
    });
    setReplyText('');
    setReplyMenciones([]);
    setReplyOpen(false);
    if (!expanded) onToggleExpanded(true);
  };

  const guardarEdicion = () => {
    if (!editText.trim()) return;
    onEditar(comentario.id, { contenido: editText.trim(), menciones: editMenciones });
    setEditOpen(false);
  };

  const renderTexto = (texto) => {
    if (!texto) return texto;
    // 1) Escapar HTML para evitar XSS
    const escapeHtml = (s) =>
      s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    let html = escapeHtml(texto);

    // 2) Convertir URLs en <a> con estilo purple
    const URL_REGEX = /(https?:\/\/[^\s()<>[\]"']+)/g;
    const TRAILING_PUNCT = /[.,;:!?)\]}>]+$/;
    html = html.replace(URL_REGEX, (raw) => {
      const m = raw.match(TRAILING_PUNCT);
      const url = m ? raw.slice(0, -m[0].length) : raw;
      const trailing = m ? m[0] : '';
      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-purple-700 hover:text-purple-900 underline underline-offset-2 break-all">${url}</a>${trailing}`;
    });

    // 3) Convertir @menciones (nombre apellido) en span violeta
    if (comentario.menciones_info && comentario.menciones_info.length > 0) {
      comentario.menciones_info.forEach((m) => {
        const label = ((m.nombre || '') + ' ' + (m.apellido || '')).trim();
        if (!label) return;
        const escapedLabel = escapeHtml(label);
        const tag = '<span class="text-violet-700 font-medium">@' + escapedLabel + '</span>';
        html = html.split('@' + escapedLabel).join(tag);
      });
    }
    return <span dangerouslySetInnerHTML={{ __html: html }} />;
  };

  const fmtFecha = comentario.created_at ? formatBsAs(comentario.created_at) : '';

  return (
    <div
      style={{ marginLeft: indentPx }}
      className={borderClass + ' ' + (nivel > 0 ? 'mt-2' : 'mt-3')}
      data-testid={'comentario-' + comentario.id}
    >
      <div className="flex items-start gap-2">
        <div className="w-8 h-8 bg-violet-100 rounded-full flex items-center justify-center text-violet-700 text-xs font-semibold flex-shrink-0">
          {(comentario.autor?.nombre?.[0] || 'U').toUpperCase()}
        </div>
        <div className="flex-1 min-w-0">
          <div className="bg-gray-100 rounded-2xl px-3 py-2 inline-block max-w-full">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-gray-900">
                {comentario.autor?.nombre} {comentario.autor?.apellido}
              </span>
              <span className="text-[10px] text-gray-400">{fmtFecha}</span>
              {comentario.editado && (
                <span className="text-[10px] text-gray-400 italic">Editado</span>
              )}
            </div>
            {editOpen ? (
              <div className="mt-1">
                <MentionInput
                  value={editText}
                  onChange={(t, m) => {
                    setEditText(t);
                    setEditMenciones(m);
                  }}
                  spaceId={spaceId}
                  token={token}
                  rows={2}
                  autoFocus
                  testId={'edit-input-' + comentario.id}
                />
                <div className="flex gap-2 mt-1">
                  <button
                    onClick={guardarEdicion}
                    className="text-xs px-3 py-1 bg-violet-600 text-white rounded hover:bg-violet-700"
                    data-testid={'save-edit-' + comentario.id}
                  >
                    Guardar
                  </button>
                  <button
                    onClick={() => {
                      setEditOpen(false);
                      setEditText(comentario.contenido);
                    }}
                    className="text-xs px-3 py-1 text-gray-600 hover:bg-gray-200 rounded"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">
                {renderTexto(comentario.contenido)}
              </p>
            )}
          </div>

          {!editOpen && (
            <ComentarioActions
              comentario={comentario}
              totalReacciones={totalReacciones}
              esAutor={esAutor}
              puedeEliminar={puedeEliminar}
              menuOpen={menuOpen}
              setMenuOpen={setMenuOpen}
              setEditOpen={setEditOpen}
              replyOpen={replyOpen}
              setReplyOpen={setReplyOpen}
              onReaccionar={onReaccionar}
              onEliminar={onEliminar}
            />
          )}

          {replyOpen && (
            <ReplyComposer
              comentario={comentario}
              spaceId={spaceId}
              token={token}
              replyText={replyText}
              setReplyText={setReplyText}
              setReplyMenciones={setReplyMenciones}
              setReplyOpen={setReplyOpen}
              enviarRespuesta={enviarRespuesta}
            />
          )}

          {hijosCount > 0 && (
            <div className="mt-2">
              {!expanded ? (
                <button
                  onClick={() => onToggleExpanded(true)}
                  className="text-xs font-medium text-violet-600 hover:underline flex items-center gap-1"
                  data-testid={'ver-respuestas-' + comentario.id}
                >
                  <MessageCircle className="w-3 h-3" />
                  Ver {hijosCount} respuesta{hijosCount !== 1 ? 's' : ''}
                </button>
              ) : (
                hijosCount > 1 && (
                  <button
                    onClick={() => onToggleExpanded(false)}
                    className="text-xs text-gray-500 hover:underline mb-1"
                  >
                    Ocultar respuestas
                  </button>
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ComentarioActions({
  comentario,
  totalReacciones,
  esAutor,
  puedeEliminar,
  menuOpen,
  setMenuOpen,
  setEditOpen,
  replyOpen,
  setReplyOpen,
  onReaccionar,
  onEliminar,
}) {
  return (
    <div className="flex items-center gap-3 mt-1 px-2 text-xs text-gray-600">
      <ReactionPicker
        currentReaction={comentario.mi_reaccion}
        onPick={(tipo) => onReaccionar(comentario.id, tipo)}
        compact
      />
      <button
        onClick={() => setReplyOpen(!replyOpen)}
        className="font-medium hover:text-violet-600"
        data-testid={'responder-' + comentario.id}
      >
        Responder
      </button>
      {totalReacciones > 0 && (
        <span className="flex items-center gap-1 text-gray-500">
          {Object.entries(comentario.reacciones || {})
            .filter((e) => e[1] > 0)
            .slice(0, 3)
            .map((e) => (
              <span key={e[0]} className="text-xs">
                {getReactionMeta(e[0]).emoji}
              </span>
            ))}
          <span>{totalReacciones}</span>
        </span>
      )}
      {(esAutor || puedeEliminar) && (
        <div className="relative ml-auto">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="p-1 hover:bg-gray-100 rounded"
            aria-label="Más opciones"
            data-testid={'menu-' + comentario.id}
          >
            <MoreVertical className="w-3.5 h-3.5" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-full mt-1 bg-white border shadow-md rounded-md py-1 z-20 min-w-[140px]">
              {esAutor && (
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    setEditOpen(true);
                  }}
                  className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50"
                  data-testid={'editar-' + comentario.id}
                >
                  <Edit2 className="w-3.5 h-3.5" /> Editar
                </button>
              )}
              {puedeEliminar && (
                <button
                  onClick={() => {
                    setMenuOpen(false);
                    onEliminar(comentario.id);
                  }}
                  className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50"
                  data-testid={'eliminar-' + comentario.id}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Eliminar
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ReplyComposer({
  comentario,
  spaceId,
  token,
  replyText,
  setReplyText,
  setReplyMenciones,
  setReplyOpen,
  enviarRespuesta,
}) {
  return (
    <div className="mt-2 ml-2">
      <MentionInput
        value={replyText}
        onChange={(t, m) => {
          setReplyText(t);
          setReplyMenciones(m);
        }}
        spaceId={spaceId}
        token={token}
        rows={2}
        autoFocus
        placeholder={'Respondé a ' + (comentario.autor?.nombre || '') + '...'}
        testId={'reply-input-' + comentario.id}
      />
      <div className="flex gap-2 mt-1">
        <button
          onClick={enviarRespuesta}
          disabled={!replyText.trim()}
          className="text-xs px-3 py-1 bg-violet-600 text-white rounded hover:bg-violet-700 disabled:opacity-50"
          data-testid={'enviar-reply-' + comentario.id}
        >
          Responder
        </button>
        <button
          onClick={() => {
            setReplyOpen(false);
            setReplyText('');
          }}
          className="text-xs px-3 py-1 text-gray-600 hover:bg-gray-200 rounded flex items-center gap-1"
        >
          <X className="w-3 h-3" /> Cancelar
        </button>
      </div>
    </div>
  );
}

/**
 * Aplana el árbol de comentarios en un array lineal con `nivel` precalculado,
 * respetando los nodos colapsados/expandidos.
 */
function buildFlat(grouped, parentKey, nivel, expandedMap, autoExpandLevel) {
  const out = [];
  const items = grouped[parentKey] || [];
  for (const c of items) {
    const hijos = grouped[c.id] || [];
    const isExpanded =
      expandedMap[c.id] !== undefined
        ? expandedMap[c.id]
        : nivel < autoExpandLevel;
    out.push({ comentario: c, nivel, hijosCount: hijos.length, expanded: isExpanded });
    if (hijos.length > 0 && isExpanded) {
      out.push(...buildFlat(grouped, c.id, nivel + 1, expandedMap, autoExpandLevel));
    }
  }
  return out;
}

export default function MuroComentarios(props) {
  const { comentarios = [], postId, ...rest } = props;
  const [expandedMap, setExpandedMap] = useState({});

  const grouped = useMemo(() => {
    const out = {};
    comentarios.forEach((c) => {
      const p = c.respuesta_a || '__root__';
      if (!out[p]) out[p] = [];
      out[p].push(c);
    });
    return out;
  }, [comentarios]);

  const flat = useMemo(
    () => buildFlat(grouped, '__root__', 0, expandedMap, MAX_INDENT),
    [grouped, expandedMap],
  );

  if (flat.length === 0) return null;

  return (
    <div className="space-y-1 mb-3" data-testid={'comments-tree-' + postId}>
      {flat.map(({ comentario, nivel, hijosCount, expanded }) => (
        <ComentarioRow
          key={comentario.id}
          comentario={comentario}
          nivel={nivel}
          hijosCount={hijosCount}
          expanded={expanded}
          onToggleExpanded={(value) =>
            setExpandedMap((prev) => ({ ...prev, [comentario.id]: value }))
          }
          {...rest}
        />
      ))}
    </div>
  );
}
